import React, { useMemo } from 'react';
import { ThreeElements } from '@react-three/fiber';

import { BearingEnvelope, Point3D as Point3DInterface } from '@paragon/contract-library';
import { calculateBearingEnvelopeShape, calculatePlyDividerSegments } from '@paragon/layout';
import { getModifierKeys } from '@paragon/utilities';

import { useAppDispatch, useAppSelector } from '../../../components/hooks';
import { convertFromDesignToLayoutCoordinates } from '../../../library/layout-geometry/coordinates';
import { getBearingEnvelopeSelectableType } from '../../../library/selection/types';
import {
  clearHoveredBearingEnvelopeEdge,
  clearHoveredBearingEnvelopeInteractiveEditHoverIfActive,
  clearHoveredBearingEnvelopeInterior,
  setHoveredBearingEnvelopeEdgeIfChanged,
  setHoveredBearingEnvelopeInteriorIfChanged,
} from '../../../store/actions/layout/bearingEnvelopeActions';
import { startBearingEnvelopeEdit } from '../../../store/actions/layout/interactiveEditingActions';
import {
  handleStandardSceneItemEditPropertiesAction,
  handleStandardSceneItemRightClick,
} from '../../../store/actions/sceneInteractionActions';
import { selectItem } from '../../../store/actions/selectActions';
import { clearHoverItemIfActive, ensureHoverItemIsActive } from '../../../store/actions/viewportActions';
import {
  getActiveBeamMode,
  getActiveNonStructuralWallMode,
  getActiveRenderModeForBearingEnvelope,
  getActiveWallMode,
} from '../../../store/selectors/layoutVisualOptionsSelectors';
import { threeJsDom } from '../../util/events/ThreeJSDom';
import { EnvelopeEndLabels } from '../EnvelopeEndLabels';
import { EnvelopePlyDividers } from '../EnvelopePlyDividers';
import { getBearingEnvelopeColors, layoutColors } from '../layoutColors';
import { getLayoutPolygonRaycastInflation } from '../raycastLayoutCoordinatesPolygon';
import { classifyBearingEnvelopeHitTarget } from './bearingEnvelopeHitTesting';
import { BearingFlatVisual } from './BearingFlatVisual';
import { BearingSolidVisual } from './BearingSolidVisual';
import { HatchingMode } from './generateEnvelopeHatchLines';

interface Props {
  envelope: BearingEnvelope;
  style: 'faded' | 'emphasised' | 'normal';

  threeDimensional: boolean;

  // If interactivity is disabled, tooltips and selection will not work. Defaults to true.
  interactive?: boolean;

  // If edit interactions are disabled, selection remains active but interactive move/resize operations are suppressed.
  editInteractionsEnabled?: boolean;

  // optional; only applicable for 2D bearing visuals
  hatchingMode?: HatchingMode;

  // optional: if set, the end label on this side will be hidden
  hideLabelOnEnd?: 'left' | 'right';

  // if true, render envelope outline edges as dashed lines (used for insertion previews)
  dashed?: boolean;
}

export function BearingEnvelopeVisual(props: Props) {
  const dispatch = useAppDispatch();

  const structuralWallMode = useAppSelector(getActiveWallMode);
  const nonStructuralWallMode = useAppSelector(getActiveNonStructuralWallMode);
  const beamMode = useAppSelector(getActiveBeamMode);
  const bearingMode = getActiveRenderModeForBearingEnvelope(
    props.envelope,
    structuralWallMode,
    nonStructuralWallMode,
    beamMode,
  );

  const shape = useMemo(() => calculateBearingEnvelopeShape(props.envelope), [props.envelope]);
  const plyDividers = useMemo(() => calculatePlyDividerSegments(props.envelope), [props.envelope]);

  if (shape == null) return null;
  if (bearingMode === 'none') return null;

  let eventHandlers: Partial<ThreeElements['group']> = {};

  if (props.interactive !== false) {
    const editInteractionsEnabled = props.editInteractionsEnabled !== false;
    const selectableType = getBearingEnvelopeSelectableType(props.envelope);
    const item = { type: selectableType, guid: props.envelope.guid, origin: 'fiber' } as const;

    function getInteractiveEditHitPoint(point: Point3DInterface) {
      const layoutPosition = convertFromDesignToLayoutCoordinates(point).projectTo2D();
      const target = classifyBearingEnvelopeHitTarget(
        layoutPosition,
        shape!,
        props.envelope,
        getLayoutPolygonRaycastInflation(),
      );

      return { layoutPosition, target };
    }

    eventHandlers = {
      onClick: (event) => {
        if (event.detail === 2) {
          event.stopPropagation();
          dispatch(handleStandardSceneItemEditPropertiesAction(selectableType, props.envelope));
        } else if (threeJsDom.shouldDispatchClickEvent()) {
          event.stopPropagation();
          dispatch(
            selectItem(props.envelope, selectableType, {
              intent: 'toggle',
              exclusive: !getModifierKeys(event).ctrlKey,
            }),
          );
        }
      },
      onContextMenu: async (event) => {
        await dispatch(handleStandardSceneItemRightClick(event, item));
      },
      onPointerOver: (event) => {
        event.stopPropagation();
        dispatch(ensureHoverItemIsActive(item));
      },
      onPointerMove: (event) => {
        if (props.threeDimensional || event.buttons !== 0) return;

        event.stopPropagation();
        const { target } = getInteractiveEditHitPoint(event.point);

        if (target?.type === 'edge') {
          dispatch(setHoveredBearingEnvelopeEdgeIfChanged(props.envelope.guid, target.edge.direction.angle));
          dispatch(clearHoveredBearingEnvelopeInterior());
        } else if (target?.type === 'interior') {
          dispatch(clearHoveredBearingEnvelopeEdge());
          dispatch(setHoveredBearingEnvelopeInteriorIfChanged(props.envelope.guid));
        } else {
          dispatch(clearHoveredBearingEnvelopeEdge());
          dispatch(clearHoveredBearingEnvelopeInterior());
        }
      },
      onPointerDown: (event) => {
        if (!editInteractionsEnabled) return;
        if (props.threeDimensional) return;
        if (event.button !== 0) return;
        if (getModifierKeys(event).ctrlKey) return;

        const { target, layoutPosition } = getInteractiveEditHitPoint(event.point);

        if (target == null) return;

        event.stopPropagation();
        if (target.type === 'edge') {
          dispatch(startBearingEnvelopeEdit(props.envelope.guid, 'end', target.edge.midpoint, layoutPosition));
        } else {
          dispatch(startBearingEnvelopeEdit(props.envelope.guid, 'interior', layoutPosition, layoutPosition));
        }
      },
      onPointerOut: () => {
        dispatch(clearHoverItemIfActive(item));
        dispatch(clearHoveredBearingEnvelopeInteractiveEditHoverIfActive(props.envelope.guid));
      },
    };
  }

  return (
    <>
      {props.threeDimensional ? (
        <>
          <BearingSolidVisual
            shape={shape}
            structural={!props.envelope.nonStructural}
            style={props.style}
            envelope={props.envelope}
            wireframe={bearingMode === 'wireframe' || bearingMode === 'hatched-wireframe'}
            dashed={props.dashed}
            {...eventHandlers}
          />

          <EnvelopePlyDividers
            segments={plyDividers}
            height={props.envelope.top}
            color={
              bearingMode === 'solid'
                ? layoutColors.solidMeshEdges
                : getBearingEnvelopeColors(props.envelope.bearingType, !props.envelope.nonStructural)[props.style]
            }
          />
        </>
      ) : (
        <BearingFlatVisual
          bearingType={props.envelope.bearingType}
          shape={shape}
          structural={!props.envelope.nonStructural}
          style={props.style}
          topElevation={props.envelope.bottom}
          solidFill={bearingMode === 'solid'}
          hatchingMode={bearingMode === 'hatched-wireframe' ? props.hatchingMode : undefined}
          plyDividers={plyDividers}
          dashed={props.dashed}
          {...eventHandlers}
        />
      )}

      {props.style === 'emphasised' && (
        <EnvelopeEndLabels
          envelope={props.envelope}
          elevation={props.envelope.bottom}
          alwaysOnTop={!props.threeDimensional}
          hiddenEndLabel={props.hideLabelOnEnd}
          leftEndLabel="Start"
          rightEndLabel="End"
        />
      )}
    </>
  );
}
